import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PLAN_DEFINITIONS, type Plan } from '@wrapforce/shared/constants';
import { useLocaleFormatters } from '@/lib/format';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useAuditLogs } from './api';

export function AuditLogCard({ workspaceId, role, plan }: { workspaceId: string; role: string; plan: string }) {
  const { t } = useTranslation('audit');
  const { formatDateTime } = useLocaleFormatters();
  const canView = role === 'owner' || role === 'manager';
  const hasAccess = PLAN_DEFINITIONS[plan as Plan].gates.auditLogUi;
  const { data } = useAuditLogs(workspaceId, canView && hasAccess);

  if (!canView) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-0 p-0">
        {!hasAccess ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted">
            <Lock size={14} /> {t('lockedMessage')}
          </div>
        ) : (
          <>
            {(data?.items ?? []).map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-sm last:border-b-0">
                <div>
                  <div className="font-medium text-ink">{t(`actions.${entry.action}`, { defaultValue: entry.action })}</div>
                  <div className="text-xs text-muted">{entry.actorName ?? t('systemActor')} · {formatDateTime(new Date(entry.createdAt))}</div>
                </div>
                {entry.ip ? <span className="font-mono text-xs text-muted">{entry.ip}</span> : null}
              </div>
            ))}
            {(data?.items ?? []).length === 0 ? <p className="p-4 text-sm text-muted">{t('empty')}</p> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
